import { useEffect, useState, type ReactNode } from 'react';
import { ArrowRight, Layers, ShieldCheck, Users, Video, Zap } from 'lucide-react';
import baseapi from '../../baseapi';
import { readLast, saveLast } from '../../utils/lastAnswer';

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-IN'));
const COUNTING = ['online', 'replay'];

/**
 * The six figures at the top of the Holding Areas page, straight from GET /api/holding:
 * people in the boxes (box cameras), people coming in and being released per minute (the areas' entry and
 * exit cameras, last 15 minutes), boxes, cameras and how full the boxes are. A figure no camera measures
 * shows "—" with the reason.
 */
export function HoldingTopTiles() {
  const [d, setD] = useState<any | null>(() => readLast<any>('holding'));

  useEffect(() => {
    let alive = true;
    const load = () => baseapi.get('holding')
      .then(r => { if (alive && Array.isArray(r.data?.areas)) { setD(r.data); saveLast('holding', r.data); } })
      .catch(() => { /* keep the last figures */ });
    load();
    const t = window.setInterval(load, 5000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  const areas: any[] = d?.areas ?? [];
  const s = d?.summary ?? {};
  const sum = (key: string) => {
    const v = areas.map(a => a[key]).filter((x: any) => typeof x === 'number');
    return v.length ? v.reduce((a: number, b: number) => a + b, 0) : null;
  };
  const entryCams = areas.flatMap(a => a.entry_cameras ?? []);
  const exitCams = areas.flatMap(a => a.exit_cameras ?? []);
  const entryOn = entryCams.some((c: any) => COUNTING.includes(c.state));
  const exitOn = exitCams.some((c: any) => COUNTING.includes(c.state));
  const in15 = sum('entered_15m'), out15 = sum('released_15m');
  const declared = areas.reduce((a, x) => a + (x.declared_capacity || 0), 0);
  const pct = s.count != null && s.capacity ? Math.round((s.count / s.capacity) * 100) : null;
  const dense = areas.reduce((a, x) => a + (x.status_counts?.dense || 0), 0);
  const critical = areas.reduce((a, x) => a + (x.status_counts?.critical || 0), 0);

  const tile = (icon: ReactNode, label: string, right: string, value: string, unit: string, sub: string, tone: string) => (
    <div className="p-1.5 px-2 rounded-md bg-neutral-50/80 dark:bg-[#14171f] border border-neutral-200/60 dark:border-[#222733] flex flex-col justify-between min-w-0">
      <div className="flex items-center justify-between gap-1 text-[10px] sm:text-[10.5px] text-neutral-500 dark:text-neutral-400 font-medium">
        <span className="flex items-center gap-1.5">{icon} {label}</span>
        <span className="text-[9.5px] font-mono text-neutral-400 truncate">{right}</span>
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className={`text-base font-bold font-mono leading-none ${tone}`}>{value}</span>
        {value !== '—' && <span className="text-[9.5px] font-medium text-neutral-400 dark:text-neutral-500">{unit}</span>}
      </div>
      <span className="text-[9px] text-neutral-400 truncate" title={sub}>{sub}</span>
    </div>
  );

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5 pt-1.5">
      {tile(<Users className="w-3 h-3 text-blue-500" />, 'Devotees', pct != null ? `${pct}%` : '',
        fmt(s.count), `/${fmt(s.capacity)} seats`, `in the boxes now · drawing labels ${fmt(declared || null)}`, 'text-neutral-900 dark:text-neutral-100')}
      {tile(<Zap className="w-3 h-3 text-cyan-500" />, 'Inflow', '15 min avg',
        entryOn && in15 != null ? `+${fmt(Math.round(in15 / 15))}` : '—', '/min',
        !entryCams.length ? 'no entry camera yet' : entryOn ? `entered today ${fmt(sum('entered_today'))}` : 'entry cameras not counting now', 'text-cyan-600 dark:text-cyan-400')}
      {tile(<ArrowRight className="w-3 h-3 text-blue-500" />, 'Release', '15 min avg',
        exitOn && out15 != null ? fmt(Math.round(out15 / 15)) : '—', '/min',
        !exitCams.length ? 'no exit camera yet' : exitOn ? `released today ${fmt(sum('released_today'))}` : 'exit camera not counting now', 'text-blue-600 dark:text-blue-400')}
      {tile(<Layers className="w-3 h-3 text-amber-500" />, 'Rooms', `${s.areas ?? areas.length} HA`,
        fmt(s.boxes), 'boxes', `${fmt(s.boxes_with_camera)} with a camera`, 'text-neutral-900 dark:text-neutral-100')}
      {tile(<Video className="w-3 h-3 text-sky-500" />, 'CCTVs', '',
        fmt(s.boxes_with_camera), 'box cams', `${fmt(s.boxes_counted)} of ${fmt(s.boxes_with_camera)} counting now`, 'text-sky-600 dark:text-sky-400')}
      {tile(<ShieldCheck className={`w-3 h-3 ${critical ? 'text-rose-500' : dense ? 'text-amber-500' : 'text-emerald-500'}`} />, 'Box status', '',
        s.boxes_counted ? (critical ? `${critical} critical` : dense ? `${dense} dense` : 'All normal') : '—', '',
        s.boxes_counted ? `of ${fmt(s.boxes_counted)} boxes measured · dense from 70%, critical from 90%` : 'no box is being measured now',
        critical ? 'text-rose-600 dark:text-rose-400' : dense ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400')}
    </div>
  );
}
